import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { BelegEntry, GbprintService, GrundstueckDetailEntryMeta, GrundstueckNummerListEntry } from '../../core/gb-api';
import { MapType } from '../../shared/enum/map-type';
import { FormatConfigService } from '../../core/services/format-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DokulinkItemBuilderFactory } from '../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { CapExceptionHandler } from '../../core/extension/exceptions/cap-exception-handler';
import { GrundstuecknummerPipe } from '../../shared/pipes/simple/grundstuecknummer.pipe';
import { keysToTranslate } from '../../../i18n/keys-to-translate';
import { sanatizedSaveAs } from '../../shared/functions/file.functions';
import { ContentHeaderComponent } from '../../shared/components/content-header/content-header.component';
import { DatumPipe } from '../../shared/pipes/simple/datum.pipe';
import { FremdsystemeComponent } from '../../shared/components/fremdsysteme/fremdsysteme.component';
import { GrundstueckArtLangPipe } from '../../shared/pipes/simple/grundstueck-art-lang.pipe';
import { KompaktGrundstueckDetailComponent } from '../../shared/kompaktdarstellung/grundstueck/grundstueck-detail/kompakt-grundstueck-detail.component';
import { KompaktTimepointComponent } from '../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { KopfdatenelementComponent } from '../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { LinkComponent } from '../../shared/components/link/link.component';
import { TitleComponent } from '../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../shared/components/title-functionbar/title-functionbar.component';
import { TranslateNamePipe } from '../../shared/pipes/simple/translate-name.pipe';
import { ErweiterungConfigResponse } from '../../core/base-api';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SafeResourceUrl } from '@angular/platform-browser';
import DokulinkVerfuegbarEnum = BelegEntry.DokulinkVerfuegbarEnum;
import GrundbuchFuehrungEnum = GrundstueckDetailEntryMeta.GrundbuchFuehrungEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-grundstueck-kopf-icp',
  imports: [
    ContentHeaderComponent,
    DatumPipe,
    FremdsystemeComponent,
    GrundstueckArtLangPipe,
    KompaktGrundstueckDetailComponent,
    KompaktTimepointComponent,
    KopfdatenelementComponent,
    LinkComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    TranslateNamePipe,
    TranslatePipe,
    NgbTooltip
  ],
  templateUrl: './grundstueck-kopf-icp.component.html',
  styleUrl: './grundstueck-kopf-icp.component.scss'
})
export class GrundstueckKopfIcpComponent extends AbstractComponent implements OnInit {
  gsEntry: GrundstueckDetailEntryMeta;
  gsNummerEntry: GrundstueckNummerListEntry;
  loaded: boolean = false;
  gsNummer: string;

  @Input()
  isEigentuemer: boolean = false;

  permissionAuftragsmodul: boolean = false; // Erweiterung erst mit CGE prüfen
  mapVerfuegbar: boolean = false;
  dokulinkVerfuegbar: DokulinkVerfuegbarEnum = 'DOKULINK_VORHANDEN';
  isDownloading: boolean = false;
  protected readonly mapType = MapType;

  constructor(
    protected appFormatConfigService: FormatConfigService,
    private readonly gbPrintService: GbprintService,
    private readonly translate: TranslateService,
    public dokuLinkBuilderFactory: DokulinkItemBuilderFactory,
    private readonly cd: ChangeDetectorRef,
    private readonly capExceptionHandler: CapExceptionHandler,
    private readonly grundstuecknummerPipe: GrundstuecknummerPipe
  ) {
    super();
    keysToTranslate<string>([
      'GeschlossenAm',
      'Auftraege',
      'FormDerFuehrung',
      'Grundbuchtyp',
      'GrundstueckArt',
      'GrundstueckNummer',
      'ErstelltMit',
      'GestrichenMit',
      'HaengigeGeschaefteAufträge',
      'HaengigeGeschaefte',
      'GrundstueckKopf.Label.Gemeinde'
    ]);
    keysToTranslate<GrundbuchFuehrungEnum>(['EIDGENOESSISCH', 'KANTONAL', 'BEREINIGUNG', 'EINFUEHRUNG']);
  }

  @Input()
  set grundstueckEntry(value: GrundstueckDetailEntryMeta) {
    this.mapVerfuegbar = true;
    this.gsEntry = value;
    this.calculateStringRepresentation();
  }

  ngOnInit(): void {
    this.calculateStringRepresentation();
  }

  createGrundstueckAuszug(): void {
    this.isDownloading = true;
    const subscription = this.gbPrintService.getGrundstueckAuszugICP(this.gsEntry.id.id, this.translate.currentLang).subscribe({
      next: (responseBody: Blob) => {
        sanatizedSaveAs(
          responseBody,
          this.translate.instant('Auszug') + ' ' + this.grundstuecknummerPipe.transform(this.gsEntry.grundstueckNummern[0]) + '.docx'
        );
      },
      error: (error) => this.capExceptionHandler.handleError(error)
    });
    subscription.add(() => (this.isDownloading = false));
  }

  private calculateStringRepresentation(): void {
    this.gsNummerEntry = this.gsEntry.grundstueckNummern[this.gsEntry.grundstueckNummern.length - 1];
    this.gsNummer = this.gsNummerEntry.nummer;
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummerZusatz);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer3);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer4);
    this.gsNummer += this.gsEntry.isKopie ? ' (' + this.translate.instant('GrundstückKopie') + ')' : '';
  }

  private combineIfNotNull(delimiter: string, nummer: any): string {
    if (nummer) {
      return delimiter + nummer.toString();
    }
    return '';
  }
}
